import { FC } from 'react';
import { Box, HStack, Image, VStack } from '@chakra-ui/react';

import { AccountingExportOption } from '__generated__/GQL';
import articlesImg from 'assets/brand/articles@2x.png';
import Heading from 'components/_core/Heading';
import SunshineIcon, { IconName } from 'components/_core/SunshineIcon';
import Text from 'components/_core/Text';
import BackButton from 'components/BackButton';
import { StepProps } from 'components/Stepper/types';
import * as Layout from 'layouts/StepperLayout';

import { useExportCreationContext } from '../../hooks/useExportCreationContext';
import * as routes from '../../routes';
import { TimeFrameType } from '../../types';
import locales from './locales';
import { formatNextMonth5th, formatTimeframeType } from './utils';

const ExportConfirmation: FC<StepProps> = ({ goBack }) => {
  const { flowConfig, sharedState } = useExportCreationContext();

  const { email, endDate, name, selectedContentIds, startDate, timeframeType } =
    sharedState;

  const flowLocales = flowConfig.isRecurring
    ? locales.recurring
    : locales.oneOff;

  const descriptionLocale = flowConfig.isRecurring
    ? locales.formatString(locales.recurring.description, {
        email,
        name,
        nextFifth: formatNextMonth5th(),
      })
    : locales.formatString(locales.oneOff.description, {
        email,
        name,
        period: formatTimeframeType(
          timeframeType as TimeFrameType,
          startDate,
          endDate,
        ),
      });

  const mapAccountingExportOptionToIcon: {
    [key in AccountingExportOption]: IconName;
  } = {
    [AccountingExportOption.BankStatements]: 'check',
    [AccountingExportOption.Invoices]: 'suitcase',
    [AccountingExportOption.Receipts]: 'receipt',
    [AccountingExportOption.Transactions]: 'payment',
  };

  return (
    <Layout.Root>
      <Layout.Body>
        <Layout.HeaderButtonsContainer>
          <BackButton onClick={goBack} to={routes.exports} />
        </Layout.HeaderButtonsContainer>

        <Layout.BodyScrollableSection>
          <Box paddingBottom="space-40">
            <Image
              alt=""
              boxSize="6.25rem"
              role="presentation"
              src={articlesImg}
            />
          </Box>

          <Heading marginBottom="space-32" size="lg">
            {flowLocales.title}
          </Heading>

          <Text marginBottom="space-24" variant="primary">
            {descriptionLocale}
          </Text>

          <Text marginBottom="space-8" variant="primary">
            {locales.contentTitle}
          </Text>

          <VStack align="flex-start" spacing="space-8">
            {selectedContentIds.map((contentId) => (
              <HStack key={contentId} spacing="space-8">
                <SunshineIcon
                  name={mapAccountingExportOptionToIcon[contentId]}
                />

                <Text fontWeight="weight-500">{locales.items[contentId]}</Text>
              </HStack>
            ))}
          </VStack>
        </Layout.BodyScrollableSection>
      </Layout.Body>
    </Layout.Root>
  );
};

export default ExportConfirmation;
